import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { isAbortError } from "../../../lib/abortError.ts";
import {
    QuinnBusinessOption,
    QuinnChatSummary,
    QuinnCopy,
    QuinnMessage,
    QuinnOpenChatResult,
    QuinnPageContext,
    QuinnPromptOptionKind,
    QuinnReplyTo,
    QuinnRuntime,
    QuinnRuntimeEventType,
    QuinnScope,
    QuinnScopeMode,
} from "../types";
import type { QuinnRatingEnum } from "../../../generated/enums.ts";
import {
    ALL_CLIENTS_SCOPE,
    ALL_CLIENTS_STORED_VALUE,
    applyRuntimeEvent,
    clearQuinnSession,
    createStreamingQuinnAnswer,
    createUserMessage,
    DEFAULT_SCOPE,
    findPrecedingUserContent,
    findPrecedingUserMessage,
    readSelectedClientId,
    scopeIsAnswerable,
    writeSelectedClientId,
} from "../utils";

type UseQuinnChatSessionParams = {
    runtime: QuinnRuntime;
    isVisible: boolean;
    scope: QuinnScope;
    pageContext: QuinnPageContext;
    setScopeState: Dispatch<SetStateAction<QuinnScope>>;
    userId: string;
};

type RunTurnOptions = {
    skipUserMessage?: boolean;
    removeMessageIds?: string[];
    replyTo?: QuinnReplyTo | null;
};

type ActiveTurn = {
    generation: number;
    controller: AbortController;
};

export function useQuinnChatSession({
    runtime,
    isVisible,
    scope,
    pageContext,
    setScopeState,
    userId,
}: UseQuinnChatSessionParams) {
    const [messages, setMessages] = useState<QuinnMessage[]>([]);
    const [chatId, setChatId] = useState<string | null>(null);
    const [chats, setChats] = useState<QuinnChatSummary[]>([]);
    const [businesses, setBusinesses] = useState<QuinnBusinessOption[]>([]);
    const [isBusy, setIsBusy] = useState(false);
    const [isHydrating, setIsHydrating] = useState(false);

    const activeTurnRef = useRef<ActiveTurn | null>(null);
    const turnGenerationRef = useRef(0);
    const openChatGenerationRef = useRef(0);
    const requestedChatIdRef = useRef<string | null>(null);
    const hasHydratedRef = useRef(false);
    const messagesRef = useRef<QuinnMessage[]>([]);
    const chatIdRef = useRef<string | null>(null);
    const chatsRef = useRef<QuinnChatSummary[]>([]);
    const scopeRef = useRef<QuinnScope>(scope);
    const pageContextRef = useRef<QuinnPageContext>(pageContext);

    messagesRef.current = messages;
    chatIdRef.current = chatId;
    chatsRef.current = chats;
    scopeRef.current = scope;
    pageContextRef.current = pageContext;

    useEffect(() => {
        hasHydratedRef.current = false;
        setMessages([]);
        setChatId(null);
        setChats([]);
        if (!userId) {
            clearQuinnSession();
        }
    }, [userId]);

    const reconcileScopeWithRoster = useCallback(
        (roster: QuinnBusinessOption[]) => {
            if (scopeRef.current.mode === QuinnScopeMode.AllClients) return;

            const storedId = readSelectedClientId(userId);
            if (storedId === ALL_CLIENTS_STORED_VALUE) {
                scopeRef.current = ALL_CLIENTS_SCOPE;
                setScopeState(ALL_CLIENTS_SCOPE);
                return;
            }

            const stored = storedId ? roster.find((entry) => entry.id === storedId) : undefined;
            const client = stored ?? roster[0];
            const nextScope: QuinnScope = client
                ? { mode: QuinnScopeMode.Client, businessId: client.id, businessLabel: client.name }
                : DEFAULT_SCOPE;

            if (storedId && !stored) writeSelectedClientId(userId, null);

            if (
                nextScope.mode === scopeRef.current.mode &&
                nextScope.businessId === scopeRef.current.businessId &&
                nextScope.businessLabel === scopeRef.current.businessLabel
            ) {
                return;
            }

            scopeRef.current = nextScope;
            setScopeState(nextScope);
        },
        [setScopeState, userId],
    );

    const restoreScopeForChat = useCallback(
        (id: string, chatList: QuinnChatSummary[] = chatsRef.current) => {
            const summary = chatList.find((chat) => chat.id === id);
            if (!summary) return;

            if (summary.mode === QuinnScopeMode.AllClients) {
                if (scopeRef.current.mode === QuinnScopeMode.AllClients) return;
                scopeRef.current = ALL_CLIENTS_SCOPE;
                setScopeState(ALL_CLIENTS_SCOPE);
                return;
            }

            if (!summary.businessId || summary.businessId === scopeRef.current.businessId) return;
            const nextScope = {
                ...scopeRef.current,
                mode: QuinnScopeMode.Client,
                businessId: summary.businessId,
                businessLabel: summary.businessLabel,
            };
            scopeRef.current = nextScope;
            setScopeState(nextScope);
        },
        [setScopeState],
    );

    useEffect(() => {
        if (!isVisible || hasHydratedRef.current) return;

        let cancelled = false;
        setIsHydrating(true);

        const roster = runtime.listBusinesses().then(
            (list) => list,
            (error) => {
                Sentry.captureException(error);
                return null;
            },
        );

        Promise.all([runtime.listChats(), roster])
            .then(([chatList, businessList]) => {
                if (cancelled) return;
                hasHydratedRef.current = true;
                setChats(chatList);

                if (businessList !== null) {
                    setBusinesses(businessList);
                    reconcileScopeWithRoster(businessList);
                }

                if (requestedChatIdRef.current) restoreScopeForChat(requestedChatIdRef.current, chatList);
            })
            .finally(() => {
                if (!cancelled) setIsHydrating(false);
            });

        return () => {
            cancelled = true;
        };
    }, [isVisible, reconcileScopeWithRoster, restoreScopeForChat, runtime, userId]);

    const isTurnActive = useCallback(() => activeTurnRef.current !== null, []);

    const runTurn = useCallback(
        async (content: string, options?: RunTurnOptions) => {
            const generation = ++turnGenerationRef.current;
            const controller = new AbortController();
            activeTurnRef.current = { generation, controller };

            const answer = createStreamingQuinnAnswer();
            const answerId = answer.id;

            setMessages((current) => {
                const withoutRemoved = options?.removeMessageIds?.length
                    ? current.filter((message) => !options.removeMessageIds!.includes(message.id))
                    : current;
                if (options?.skipUserMessage) {
                    return [...withoutRemoved, answer];
                }
                return [...withoutRemoved, createUserMessage(content, options?.replyTo ?? null), answer];
            });
            setIsBusy(true);

            const isCurrentTurn = () => activeTurnRef.current?.generation === generation;

            function patchAnswer(patch: (message: QuinnMessage) => QuinnMessage) {
                if (!isCurrentTurn()) return;
                setMessages((current) =>
                    current.map((message) => (message.id === answerId ? patch(message) : message)),
                );
            }

            try {
                for await (const event of runtime.send({
                    content,
                    replyTo: options?.replyTo ?? null,
                    chatId: chatIdRef.current,
                    scope: scopeRef.current,
                    pageContext: pageContextRef.current,
                    signal: controller.signal,
                })) {
                    if (!isCurrentTurn() || controller.signal.aborted) break;

                    if (event.type === QuinnRuntimeEventType.ChatStarted) {
                        setChatId(event.chatId);
                    } else {
                        patchAnswer((message) => applyRuntimeEvent(message, event));
                    }
                }
            } catch (error: unknown) {
                if (!isCurrentTurn()) return;
                if (!isAbortError(error)) {
                    patchAnswer((message) => ({
                        ...message,
                        isStreaming: false,
                        error: QuinnCopy.GenericError,
                    }));
                }
            } finally {
                if (!isCurrentTurn()) return;

                patchAnswer((message) => ({ ...message, isStreaming: false }));
                setIsBusy(false);
                activeTurnRef.current = null;

                void runtime.listChats().then((chatList) => {
                    if (!isTurnActive()) setChats(chatList);
                });
            }
        },
        [isTurnActive, runtime],
    );

    const send = useCallback(
        (content: string, replyTo?: QuinnReplyTo) => {
            const trimmed = content.trim();
            if (!trimmed || isTurnActive()) return;
            const artifactClientId = replyTo?.clientBusinessId;
            if (replyTo?.drawnForAllClients && scopeRef.current.mode !== QuinnScopeMode.AllClients) {
                scopeRef.current = ALL_CLIENTS_SCOPE;
                setScopeState(ALL_CLIENTS_SCOPE);
            } else if (artifactClientId && artifactClientId !== scopeRef.current.businessId) {
                const client = businesses.find((business) => business.id === artifactClientId);
                const nextScope = {
                    mode: QuinnScopeMode.Client,
                    businessId: artifactClientId,
                    businessLabel: client?.name ?? scopeRef.current.businessLabel,
                };
                scopeRef.current = nextScope;
                setScopeState(nextScope);
            }
            if (!scopeIsAnswerable(scopeRef.current)) return;
            void runTurn(trimmed, { replyTo: replyTo ?? null });
        },
        [businesses, isTurnActive, runTurn, setScopeState],
    );

    const stop = useCallback(() => {
        activeTurnRef.current?.controller.abort();
    }, []);

    const retry = useCallback(
        (messageId: string) => {
            if (isTurnActive()) return;
            const question = findPrecedingUserMessage(messagesRef.current, messageId);
            if (!question?.content || !scopeIsAnswerable(scopeRef.current)) return;
            void runTurn(question.content, {
                skipUserMessage: true,
                removeMessageIds: [messageId],
                replyTo: question.replyTo ?? null,
            });
        },
        [isTurnActive, runTurn],
    );

    const resetConversation = useCallback(() => {
        activeTurnRef.current?.controller.abort();
        activeTurnRef.current = null;
        openChatGenerationRef.current += 1;
        requestedChatIdRef.current = null;
        setMessages([]);
        setChatId(null);
        setIsBusy(false);
        setIsHydrating(false);
    }, []);

    const openChat = useCallback(
        (id: string): Promise<QuinnOpenChatResult> => {
            activeTurnRef.current?.controller.abort();
            activeTurnRef.current = null;
            const generation = ++openChatGenerationRef.current;
            requestedChatIdRef.current = id;
            setIsHydrating(true);
            return runtime
                .openChat(id)
                .then((loaded): QuinnOpenChatResult => {
                    if (generation !== openChatGenerationRef.current) return "superseded";
                    if (loaded === null) {
                        requestedChatIdRef.current = chatIdRef.current;
                        return "not_found";
                    }
                    setMessages(loaded);
                    setChatId(id);
                    restoreScopeForChat(id);
                    return "opened";
                })
                .catch((): QuinnOpenChatResult => {
                    if (generation !== openChatGenerationRef.current) return "superseded";
                    setMessages([
                        {
                            ...createStreamingQuinnAnswer(),
                            isStreaming: false,
                            error: QuinnCopy.GenericError,
                        },
                    ]);
                    setChatId(id);
                    return "opened";
                })
                .finally(() => {
                    if (generation === openChatGenerationRef.current) {
                        setIsHydrating(false);
                    }
                });
        },
        [restoreScopeForChat, runtime],
    );

    const rateAnswer = useCallback(
        (runId: string, rating: QuinnRatingEnum) => {
            const previous = messagesRef.current.find((message) => message.runId === runId)?.rating ?? null;
            const chosen = previous === rating ? null : rating;
            setMessages((current) =>
                current.map((message) => (message.runId === runId ? { ...message, rating: chosen } : message)),
            );
            void runtime.rateAnswer(runId, chosen).catch((error: unknown) => {
                Sentry.captureException(error);
                // Only undo this click. A later click has already replaced what we optimistically
                // set, and reverting then would show a rating the server does not hold.
                setMessages((current) =>
                    current.map((message) =>
                        message.runId === runId && message.rating === chosen
                            ? { ...message, rating: previous }
                            : message,
                    ),
                );
            });
        },
        [runtime],
    );

    const answerPrompt = useCallback(
        (messageId: string, values: string[], label: string) => {
            if (isTurnActive()) return;

            const target = messagesRef.current.find((message) => message.id === messageId);
            if (!target?.prompt || target.prompt.answeredWith) return;

            setMessages((current) =>
                current.map((message) =>
                    message.id === messageId && message.prompt
                        ? { ...message, prompt: { ...message.prompt, answeredWith: values } }
                        : message,
                ),
            );

            const promptKind = target.prompt.kind;
            if (promptKind === QuinnPromptOptionKind.Business) {
                const business = businesses.find((entry) => entry.id === values[0]);
                if (business) {
                    const nextScope = {
                        ...scopeRef.current,
                        mode: QuinnScopeMode.Client,
                        businessId: business.id,
                        businessLabel: business.name,
                    };
                    scopeRef.current = nextScope;
                    setScopeState(nextScope);
                }

                const originalQuestion = findPrecedingUserContent(messagesRef.current, messageId);
                if (!originalQuestion) return;
                void runTurn(originalQuestion, { skipUserMessage: true });
                return;
            }

            void runTurn(label, { skipUserMessage: true });
        },
        [businesses, isTurnActive, runTurn, setScopeState],
    );

    return {
        messages,
        chatId,
        chats,
        businesses,
        isBusy,
        isHydrating,
        send,
        stop,
        retry,
        startNewChat: resetConversation,
        openChat,
        answerPrompt,
        rateAnswer,
    };
}
